/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { MmcGetDispatchCommandStatusDto } from "app/models/mmc/mmc-get-dispatch-command-status-dto";
import { MmcPrintDispatch } from "app/models/mmc/mmc-print-dispatch";
import { BuchananPrintDto, BuchananService } from "app/services/buchanan.service";
import { MmcService } from "app/services/mmc.service";

@Component({
  templateUrl: './buchanan.print.component.html',
  styleUrls: ['./buchanan.print.component.css']
})
export class BuchananPrintComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Printer selected in the UI */
  selectedPrinter: string = null;

  /** Item Code entered in the UI */
  itemCode: string = "";

  /** Quantity enter in the UI */
  quantity: number = 1;

  /** Family Name entered in UI */
  familyName: string = null;

  /** PO Number entered in UI */
  po: string = null;

  /** Warehouse info entered in UI */
  warehouse: string = null;

  /** Available Printers.  Used to populate the Printers combobox */
  availablePrinters: string[] = [];

  /** Message to display to the User indicating some sort of condition */
  printMessage: string = null;
  
  /** Condition of the Message being displayed to the User.  Should only be one of the following: pending, error, success */
  printStatus: string = null;

  /** Print Dispatch which was spawned in MMC for the last print */
  mmcPrintDispatch: MmcPrintDispatch = null;

  /** Flag used to indicate whether the Printers are being loaded or not */
  isLoadingPrinters: boolean = true;

  /** Flag indicating if the Print call is currently being processed */
  isPrinting: boolean = false;

  /** Flag indicating if the component has been destroyed */
  isDestroyed: boolean = false;

  /** Constructor */
  constructor(
    private buchananService: BuchananService,
    private mmcService: MmcService
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    // Load the available printers
    this.isLoadingPrinters = true;
    this.loadPrinters();
  }

  /**
   * Loads the Printers available to the User
   */
  private async loadPrinters(): Promise<void> {
    let printers: string[] = null;
    try {
      printers = await this.mmcService.getPrinters();
    } catch (error) {
      this.handleError(error);
      return;
    }
    while (this.availablePrinters.length > 0) {
      this.availablePrinters.pop();
    }
    if (printers?.length > 0) {
      printers.sort();
      this.availablePrinters.push(...printers);
    }

    this.isLoadingPrinters = false;
  }

  /**
   * Destroys the component.  Should be used to clean up anything needed. 
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    // Set the isDestroyed flag to true so that any background processes refreshing the print status will be stopped
    this.isDestroyed = true;
  }

  /**
   * Called to handle errors when calling APIs.  
   * Displays the error to the User
   */
  handleError(error: any): void {
    this.printStatus = 'error';
    this.printMessage = `Error: ${this.getErrorString(error)}`;
  }

  /**
   * Returns the String to use for the given Error
   */
  getErrorString(error: any): string {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      return error.error;
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      response.text().then(
        s => {
          return s;
        }
      );
    }
  }

  /**
   * Called when the "Print" UI Button is clicked.
   * Displays the Progress Dialog and calls the "Bulk Print" API in the background.
   */
  printClicked(): void {
    console.log(`Print Clicked!`);
    if (!this.isPrinting) {
      this.isPrinting = true;
      this.indefiniteProgressDialog.show("Printing", "Printing Label(s)...");
      this._print();
    }
  }

  /**
   * Calls the "Print" API and handles the results.
   */
  private async _print(): Promise<void> {
    // Reset some things just in case
    this.mmcPrintDispatch = null;
    this.printMessage = null;
    this.printStatus = null;

    // Build the DTO to call the API with
    const dto = {
      item_code: this.itemCode,
      printer: this.selectedPrinter,
      quantity: this.quantity,
      family_name: this.familyName,
      po: this.po,
      warehouse: this.warehouse
    } as BuchananPrintDto;

    // Call the API
    try {
      this.mmcPrintDispatch = await this.buchananService.print(dto);
    } catch(error) {
      this.indefiniteProgressDialog.hide();
      this.isPrinting = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.isPrinting = false;

    if (this.mmcPrintDispatch == null) {
      this.printStatus = "pending";
      this.printMessage = "Print processed but not printed due to configuration.";
    } else if (this.mmcPrintDispatch?.dispatches?.length > 0 && this.mmcPrintDispatch.dispatches[0].id != null) {
      this.printStatus = 'pending';
      this.printMessage = 'Pending...';
      setTimeout(this.refreshDispatch.bind(this, this.mmcPrintDispatch), 300);
    } else {
      this.printStatus = 'error';
      this.printMessage = 'Error retrieving Dispatch';
    }
  }

  /**
   * Retrieves the latest status of the given MmcPrintDispatch if it was the last
   * one that was printed.  
   */
  private async refreshDispatch(printDispatch: MmcPrintDispatch): Promise<void> {
    // Cancel this process for the given MmcPrintDispatch for a variety of reasons...
    if (this.isDestroyed) {
      console.warn(`Stopping Print Dispatch refresh as the Component was destroyed!`);
      return;
    } else if (printDispatch == null) {
      console.warn(`Stopping Print Dispatch refresh as the this Dispatch is null!`);
      return;
    } else if (printDispatch != this.mmcPrintDispatch) {
      console.warn(`Stopping Print Dispatch refresh as the this Dispatch doesn't match!`);
      return;
    }

    // Call the API to get the latest status
    const dispatchId = printDispatch.dispatches[0].id;
    let commandStatus: MmcGetDispatchCommandStatusDto = null;
    try {
      commandStatus = await this.mmcService.getDispatchCommandStatus(dispatchId);
    } catch (error) {
      console.error(`Error obtaining Dispatch Status for ID: '${dispatchId}'... Error: ${error}`);
      // Don't error here, just try again... TODO: Might be a better way of handling this... maybe after X retries, show a message to the User to indicate something is wrong?
    }

    // Update the Print Status / Message based on the latest status (if it should be done)
    if (!this.isDestroyed && printDispatch == this.mmcPrintDispatch) {
      if (commandStatus?.printStatus != null) {
        this.printStatus = commandStatus.printStatus;

        if (this.printStatus == 'success') {
          this.printMessage = 'Success!';
        } else if (this.printStatus == 'error') {
          this.printMessage = 'Problem printing label(s)...';
        } else {
          // Schedule another refresh
          setTimeout(this.refreshDispatch.bind(this, printDispatch), 300);
        }
      }
    }
  }


}
